import React, { useState, useEffect } from 'react';
import { FiX, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Status options for the filter
const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'reported', label: 'Reported' },
  { value: 'paid', label: 'Paid' },
  { value: 'resolved', label: 'Resolved' },
];

const DamageReports = () => {
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingDamage, setEditingDamage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    amount: '',
    status: 'reported',
  });
  const [editErrors, setEditErrors] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch damage reports
  const fetchDamages = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (bookingIdFilter) params.append('bookingid', bookingIdFilter);

      const response = await fetch(`${BQ_BASE_URL}/bq/api/damages?${params.toString()}`);

      if (!response.ok) throw new Error('Failed to fetch damage reports');

      const data = await response.json();
      setDamages(data);
      setTotalPages(Math.ceil(data.length / itemsPerPage));
      setCurrentPage(1); // Reset to first page when filters change
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/damages/stats/status`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchDamages();
    fetchStats();
  }, [statusFilter, bookingIdFilter]);

  // Pagination functions
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return damages.slice(startIndex, endIndex);
  };

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 4);
    const endPage = Math.min(totalPages, currentPage + 5);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle filter changes
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleBookingIdChange = (e) => {
    setBookingIdFilter(e.target.value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get status color based on status value
  const getStatusColor = (status) => {
    switch (status) {
      case 'reported':
        return 'bg-yellow-100 text-yellow-800';
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle image view
  const handleViewImage = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Validate edit form
  const validateEditForm = () => {
    const errors = {};

    if (!editForm.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
      errors.amount = 'Valid amount is required';
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle edit damage
  const handleEdit = (damage) => {
    setEditingDamage(damage);
    setEditForm({
      title: damage.title,
      description: damage.description || '',
      amount: damage.amount,
      status: damage.status,
    });
    setEditErrors({});
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Submit edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!validateEditForm()) {
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/damages/${editingDamage.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) throw new Error('Failed to update damage report');

      fetchDamages(); // Refresh the list
      setEditingDamage(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (damage) => {
    setDeleteConfirm(damage);
  };

  // Handle delete damage
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/damages/${deleteConfirm.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete damage report');

      fetchDamages(); // Refresh the list
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    }
  };

  const formatCurrency = (value) => {
    return value ? `${Number(value).toFixed(2)}` : '0.00';
  };

  const currentData = getCurrentPageData();

  return (
    <div className="mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      <NavLink
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to Front Desk Operations</span>
      </NavLink>
      <div className="px-10 mt-3">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Damage Reports</h1>

        {/* Stats Overview */}
        {stats && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Damage Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-500">Total Damages</p>
                <p className="text-2xl font-bold">{stats.total_damages}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold">₹{formatCurrency(stats.total_amount)}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-500">Average Amount</p>
                <p className="text-2xl font-bold">₹{formatCurrency(stats.average_amount)}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Status Distribution</p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                    Reported: {stats.status_counts?.reported || 0}
                  </span>
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    Paid: {stats.status_counts?.paid || 0}
                  </span>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                    Resolved: {stats.status_counts?.resolved || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="statusFilter"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={handleStatusChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg duration-200"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="bookingIdFilter"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Booking ID
              </label>
              <input
                type="text"
                id="bookingIdFilter"
                value={bookingIdFilter}
                onChange={handleBookingIdChange}
                placeholder="Search by booking ID"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg duration-200"
              />
            </div>
          </div>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Damage Reports List */}
        {!loading && !error && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Booking ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date Reported
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData.length > 0 ? (
                    currentData.map((damage) => (
                      <tr key={damage.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {damage.bookingid}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {damage.title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {damage.description || 'No description provided'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ₹{damage.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getStatusColor(
                              damage.status
                            )}`}
                          >
                            {damage.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(damage.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-3">
                            {damage.image_url && (
                              <button
                                onClick={() => handleViewImage(damage.image_url)}
                                className="text-gray-600 hover:text-black transition-colors duration-200"
                                title="View Image"
                              >
                                <FiEye className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(damage)}
                              className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                              title="Edit"
                            >
                              <FiEdit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(damage)}
                              className="text-gray-600 hover:text-red-600 transition-colors duration-200"
                              title="Delete"
                            >
                              <FiTrash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg
                            className="h-12 w-12 text-gray-400 mb-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <p className="text-gray-500">No damage reports found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && damages.length > 0 && (
          <div className="flex justify-center mt-8 text-xs gap-2 items-center">
            {/* Previous Button */}
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors duration-200"
            >
              Previous
            </button>

            {/* Show first page and ellipsis if not in first group */}
            {currentPage > 10 && (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px] transition-colors duration-200"
                >
                  1
                </button>
                <span className="px-2">...</span>
              </>
            )}

            {/* Page Numbers - Show 10 pages at a time */}
            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-2 py-2 mx-1 min-w-[35px] transition-colors duration-200 ${
                  currentPage === page
                    ? 'bg-black text-white rounded-full'
                    : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
                }`}
              >
                {page}
              </button>
            ))}

            {/* Show last page and ellipsis if not in last group */}
            {currentPage < totalPages - 9 && (
              <>
                <span className="px-2">...</span>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px] transition-colors duration-200"
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next Button */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors duration-200"
            >
              Next
            </button>
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-screen overflow-auto">
              <div className="flex justify-between items-center border-b p-4">
                <h3 className="text-lg font-medium">Damage Image</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              <div className="p-4">
                <img
                  src={selectedImage}
                  alt="Damage"
                  className="max-w-full max-h-[80vh] mx-auto rounded"
                />
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingDamage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center border-b p-4">
                <h3 className="text-lg font-medium">Edit Damage Report</h3>
                <button
                  onClick={() => setEditingDamage(null)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-4">
                <div className="mb-4">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={editForm.title}
                    onChange={handleEditChange}
                    placeholder="Enter damage title"
                    className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
                      editErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editErrors.title && (
                    <p className="text-red-500 text-xs mt-1">{editErrors.title}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={editForm.description}
                    onChange={handleEditChange}
                    rows={3}
                    placeholder="Describe the damage details"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg duration-200"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={editForm.amount}
                    onChange={handleEditChange}
                    step="0.01"
                    min="0"
                    placeholder="Enter damage amount"
                    className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
                      editErrors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editErrors.amount && (
                    <p className="text-red-500 text-xs mt-1">{editErrors.amount}</p>
                  )}
                </div>
                <div className="mb-6">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={editForm.status}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg duration-200"
                  >
                    <option value="reported">Reported</option>
                    <option value="paid">Paid</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditingDamage(null)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-black hover:bg-gray-800 transition-colors duration-200"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center border-b p-4">
                <h3 className="text-lg font-medium">Confirm Delete</h3>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete the damage report for booking{' '}
                  <strong>{deleteConfirm.bookingid}</strong>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Title:</strong> {deleteConfirm.title}
                  </p>
                  <p className="text-sm text-red-700">
                    <strong>Amount:</strong> ₹{deleteConfirm.amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-red-600 mt-1">This action cannot be undone.</p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DamageReports;
